import { useEffect } from 'react'
import { router, Link } from '@inertiajs/react'
import { useTranslation } from 'react-i18next'
import { AdminLayout } from '../../../layouts/admin-layout'
import { AppHeader } from '../../../components/app-header'
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from '../../../components/ui/card'
import { Badge } from '../../../components/ui/badge'
import { Button } from '../../../components/ui/button'
import { Clock, CheckCircle, XCircle, Ban, Terminal, Play, StopCircle } from 'lucide-react'

interface User {
  id: number
  first_name: string
  last_name: string
  email: string
}

interface Execution {
  id: number
  task_name: string
  parameters: Record<string, string>
  status: string
  output_log: string | null
  error_log: string | null
  exit_code: number | null
  started_at: string | null
  completed_at: string | null
  created_at: string
  duration_display: string | null
  'cancellable?': boolean
  user: User
}

interface TaskConfig {
  description: string
  category: string
  destructive: boolean
  confirmation_message?: string
  required_env_vars: string[]
  parameters: Array<{
    name: string
    type: string
    required: boolean
    description: string
  }>
}

interface AdminRakeTaskShowProps {
  execution: Execution
  task_config: TaskConfig | null
  auth: {
    user: {
      id: number
      name: string
      email: string
      admin: boolean
      super_admin?: boolean
    }
  }
  flash?: {
    success?: string
    error?: string
  }
}

function Show({ execution, task_config }: AdminRakeTaskShowProps) {
  const { t } = useTranslation('admin')

  // Auto-refresh every 3 seconds if running
  useEffect(() => {
    if (execution.status === 'running' || execution.status === 'pending') {
      const interval = setInterval(() => {
        router.reload({ only: ['execution'] })
      }, 3000)
      return () => clearInterval(interval)
    }
  }, [execution.status])

  const getStatusBadge = (status: string) => {
    const statusConfig: Record<string, { variant: "success" | "warning" | "info" | "destructive" | "secondary"; icon: typeof CheckCircle }> = {
      'pending': { variant: 'secondary', icon: Clock },
      'running': { variant: 'info', icon: Terminal },
      'completed': { variant: 'success', icon: CheckCircle },
      'failed': { variant: 'destructive', icon: XCircle },
      'cancelled': { variant: 'warning', icon: Ban },
    }

    const config = statusConfig[status] || { variant: 'secondary' as const, icon: Clock }
    const Icon = config.icon

    return (
      <Badge variant={config.variant} className="gap-1 text-sm px-3 py-1">
        <Icon className="h-4 w-4" />
        {status.toUpperCase()}
      </Badge>
    )
  }

  const formatDateTime = (dateStr: string | null) => {
    if (!dateStr) return '-'
    return new Date(dateStr).toLocaleString('en-US', {
      month: 'short',
      day: 'numeric',
      year: 'numeric',
      hour: '2-digit',
      minute: '2-digit',
      second: '2-digit',
    })
  }

  const handleCancel = () => {
    if (confirm(t('rakeTasks.confirmCancel'))) {
      router.post(`/admin/rake_tasks/${execution.id}/cancel`)
    }
  }

  const handleRerun = () => {
    const params = new URLSearchParams({ task_name: execution.task_name })
    router.visit(`/admin/rake_tasks/new?${params.toString()}`)
  }

  return (
    <>
      <AppHeader breadcrumbs={[
        { label: 'Admin', href: '/admin' },
        { label: t('rakeTasks.title'), href: '/admin/rake_tasks' },
        { label: execution.task_name }
      ]} />

      <div className="flex flex-1 flex-col">
        <div className="@container/main flex flex-1 flex-col gap-2">
          <div className="flex flex-col gap-4 py-4 md:gap-6 md:py-6">
            <div className="px-4 lg:px-6">
              {/* Page Header */}
              <div className="mb-6 flex items-center justify-between">
                <div className="flex items-center gap-4">
                  <div>
                    <h1 className="text-2xl font-bold text-foreground font-mono">{execution.task_name}</h1>
                    <p className="mt-1 text-sm text-muted-foreground">
                      {task_config?.description || t('rakeTasks.executionDetails')}
                    </p>
                  </div>
                  {getStatusBadge(execution.status)}
                </div>
                <Button variant="outline" asChild>
                  <Link href="/admin/rake_tasks">{t('rakeTasks.backToTasks')}</Link>
                </Button>
              </div>

              <div className="grid grid-cols-1 lg:grid-cols-3 gap-6">
                {/* Main Content */}
                <div className="lg:col-span-2 space-y-6">
                  {/* Output Log */}
                  <Card className="shadow-none">
                    <CardHeader>
                      <CardTitle>{t('rakeTasks.outputLog')}</CardTitle>
                      <CardDescription>{t('rakeTasks.stdoutDescription')}</CardDescription>
                    </CardHeader>
                    <CardContent>
                      {execution.output_log ? (
                        <pre className="text-xs bg-muted/50 border rounded p-4 overflow-x-auto max-h-96 overflow-y-auto font-mono whitespace-pre-wrap">
                          {execution.output_log}
                        </pre>
                      ) : (
                        <p className="text-sm text-muted-foreground italic">{t('rakeTasks.noOutput')}</p>
                      )}
                    </CardContent>
                  </Card>

                  {/* Error Log */}
                  {execution.error_log && (
                    <Card className="shadow-none border-destructive/30 bg-destructive/5">
                      <CardHeader>
                        <CardTitle className="text-destructive">{t('rakeTasks.errorLog')}</CardTitle>
                        <CardDescription>{t('rakeTasks.stderrDescription')}</CardDescription>
                      </CardHeader>
                      <CardContent>
                        <pre className="text-xs bg-destructive/10 border border-destructive/30 rounded p-4 overflow-x-auto max-h-60 overflow-y-auto font-mono whitespace-pre-wrap">
                          {execution.error_log}
                        </pre>
                      </CardContent>
                    </Card>
                  )}

                  {/* Parameters */}
                  {Object.keys(execution.parameters).length > 0 && (
                    <Card className="shadow-none">
                      <CardHeader>
                        <CardTitle>{t('rakeTasks.parameters')}</CardTitle>
                      </CardHeader>
                      <CardContent>
                        <div className="space-y-2">
                          {Object.entries(execution.parameters).map(([key, value]) => (
                            <div key={key} className="flex items-center gap-4">
                              <span className="font-mono text-sm font-medium">{key}:</span>
                              <span className="font-mono text-sm text-muted-foreground">
                                {value.length > 50 ? `${value.substring(0, 50)}...` : value}
                              </span>
                            </div>
                          ))}
                        </div>
                      </CardContent>
                    </Card>
                  )}
                </div>

                {/* Sidebar */}
                <div className="space-y-6">
                  {/* Actions */}
                  <Card className="shadow-none">
                    <CardHeader>
                      <CardTitle>{t('rakeTasks.actions')}</CardTitle>
                    </CardHeader>
                    <CardContent className="space-y-3">
                      {execution['cancellable?'] && (
                        <Button
                          variant="outline"
                          className="w-full justify-start text-destructive hover:text-destructive hover:bg-destructive/10"
                          onClick={handleCancel}
                        >
                          <StopCircle className="h-4 w-4 mr-2" />
                          {t('rakeTasks.cancelExecution')}
                        </Button>
                      )}
                      <Button
                        variant="outline"
                        className="w-full justify-start"
                        onClick={handleRerun}
                      >
                        <Play className="h-4 w-4 mr-2" />
                        {t('rakeTasks.runAgain')}
                      </Button>
                    </CardContent>
                  </Card>

                  {/* Live Update Notice */}
                  {(execution.status === 'running' || execution.status === 'pending') && (
                    <Card className="shadow-none border-blue-500/20 bg-blue-500/5">
                      <CardContent className="pt-6">
                        <div className="flex items-center gap-2 text-sm text-muted-foreground">
                          <div className="h-2 w-2 rounded-full bg-blue-500 animate-pulse"></div>
                          {t('rakeTasks.autoRefreshingDetails')}
                        </div>
                      </CardContent>
                    </Card>
                  )}

                  {/* Details */}
                  <Card className="shadow-none">
                    <CardHeader>
                      <CardTitle>{t('rakeTasks.details')}</CardTitle>
                    </CardHeader>
                    <CardContent className="space-y-4">
                      <div>
                        <p className="text-sm font-medium text-muted-foreground">{t('rakeTasks.executedBy')}</p>
                        <p className="mt-1 text-sm text-foreground">
                          {execution.user.first_name} {execution.user.last_name}
                        </p>
                        <p className="text-xs text-muted-foreground">{execution.user.email}</p>
                      </div>
                      <div>
                        <p className="text-sm font-medium text-muted-foreground">{t('rakeTasks.createdAt')}</p>
                        <p className="mt-1 text-sm text-foreground">
                          {formatDateTime(execution.created_at)}
                        </p>
                      </div>
                      <div>
                        <p className="text-sm font-medium text-muted-foreground">{t('rakeTasks.startedAt')}</p>
                        <p className="mt-1 text-sm text-foreground">
                          {formatDateTime(execution.started_at)}
                        </p>
                      </div>
                      {execution.completed_at && (
                        <div>
                          <p className="text-sm font-medium text-muted-foreground">{t('rakeTasks.completedAt')}</p>
                          <p className="mt-1 text-sm text-foreground">
                            {formatDateTime(execution.completed_at)}
                          </p>
                        </div>
                      )}
                      {execution.duration_display && (
                        <div>
                          <p className="text-sm font-medium text-muted-foreground">{t('rakeTasks.duration')}</p>
                          <p className="mt-1 text-sm text-foreground font-mono">
                            {execution.duration_display}
                          </p>
                        </div>
                      )}
                      {execution.exit_code !== null && (
                        <div>
                          <p className="text-sm font-medium text-muted-foreground">{t('rakeTasks.exitCode')}</p>
                          <p className={`mt-1 text-sm font-mono ${execution.exit_code === 0 ? 'text-green-600' : 'text-red-600'}`}>
                            {execution.exit_code}
                          </p>
                        </div>
                      )}
                      {task_config && (
                        <div>
                          <p className="text-sm font-medium text-muted-foreground">{t('rakeTasks.category')}</p>
                          <p className="mt-1 text-sm text-foreground">
                            {task_config.category}
                          </p>
                        </div>
                      )}
                    </CardContent>
                  </Card>
                </div>
              </div>
            </div>
          </div>
        </div>
      </div>
    </>
  )
}

Show.layout = (page: React.ReactNode) => <AdminLayout>{page}</AdminLayout>

export default Show
